import React, { useState, useCallback, useRef, useEffect } from 'react';
import JSZip from 'jszip';
import heic2any from 'heic2any';
import { ImageFormat } from '../types';
import { UploadIcon } from './icons/UploadIcon';
import { JpgIcon } from './icons/JpgIcon';
import { PngIcon } from './icons/PngIcon';
import { DownloadIcon } from './icons/DownloadIcon';

const EXTENSION_ID = 'your-extension-id-goes-here';
const MAX_FREE_BATCH = 10;

declare global {
  interface Window {
    chrome?: any;
    gtag?: (...args: any[]) => void;
  }
}

const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

const isHeicFile = (file: File): boolean => {
  return file.type === 'image/heic' || 
         file.type === 'image/heif' || 
         file.name.toLowerCase().endsWith('.heic') ||
         file.name.toLowerCase().endsWith('.heif');
};

const convertHeicToBlob = async (file: File): Promise<Blob> => {
  try {
    const result = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.95,
    });
    return Array.isArray(result) ? result[0] : result;
  } catch (error) {
    console.error('HEIC conversion error:', error);
    throw new Error('Failed to convert HEIC file. Please try another file.');
  }
};

interface ProcessedFile {
  original: File;
  previewUrl: string;
  convertedBlob: Blob | null;
  status: 'pending' | 'converting' | 'done' | 'error';
  error?: string;
}

const ImageConverter: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([]);
  const [outputFormat, setOutputFormat] = useState<ImageFormat>(ImageFormat.JPEG);
  const [quality, setQuality] = useState<number>(95);
  const [resizeEnabled, setResizeEnabled] = useState<boolean>(false);
  const [resizeWidth, setResizeWidth] = useState<string>('');
  const [resizeHeight, setResizeHeight] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const imageUrl = urlParams.get('imageUrl');
    if (imageUrl) {
      setError('Loading image from extension...');
      fetchImageViaExtension(imageUrl);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchImageViaExtension = (url: string) => {
    if (window.chrome && window.chrome.runtime) {
      window.chrome.runtime.sendMessage(EXTENSION_ID, { type: 'FETCH_IMAGE', url }, 
        async (response: any) => {
          if (response && response.dataUrl) {
            try {
              const blob = await (await fetch(response.dataUrl)).blob();
              const fileName = url.substring(url.lastIndexOf('/') + 1) || 'image.webp';
              const file = new File([blob], fileName, { type: blob.type || 'image/webp' });
              handleFileSelect([file]);
            } catch (e) {
              setError("Could not process the image from extension.");
            }
          } else {
            setError("Failed to fetch image. Is the PixelShift extension installed and updated?");
            console.error(response?.error || 'No response from extension.');
          }
        }
      );
    } else {
      setError("Install the PixelShift browser extension to convert images from the web directly.");
    }
  };

  const resetState = () => {
    files.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setFiles([]);
    setError(null);
    setIsConverting(false);
  };

  const handleFileSelect = useCallback(async (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return;

    // Check batch limit
    if (selectedFiles.length > MAX_FREE_BATCH) {
      setError(`Free tier limited to ${MAX_FREE_BATCH} images at once. Selected: ${selectedFiles.length}`);
      trackEvent('batch_limit_reached', {
        attempted: selectedFiles.length,
        limit: MAX_FREE_BATCH,
      });
      return;
    }

    setError(null);
    const processedFiles: ProcessedFile[] = [];

    for (const file of selectedFiles) {
      if (!file.type.startsWith('image/') && !isHeicFile(file)) {
        setError(`Skipped ${file.name}: Invalid file type`);
        continue;
      }

      try {
        let previewUrl: string;
        
        if (isHeicFile(file)) {
          const convertedBlob = await convertHeicToBlob(file);
          previewUrl = URL.createObjectURL(convertedBlob);
        } else {
          previewUrl = URL.createObjectURL(file);
        }

        processedFiles.push({
          original: file,
          previewUrl,
          convertedBlob: null,
          status: 'pending',
        });
      } catch (err) {
        setError(`Failed to process ${file.name}`);
      }
    }

    setFiles(processedFiles);

    trackEvent('files_selected', {
      count: processedFiles.length,
      types: processedFiles.map(f => f.original.type),
      is_batch: processedFiles.length > 1,
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFileSelect(selectedFiles);
  };

  const handleDragEvents = (e: React.DragEvent<HTMLDivElement>, isOver: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(isOver);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    handleDragEvents(e, false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileSelect(droppedFiles);
  };

  const convertSingleImage = async (file: ProcessedFile): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = file.previewUrl;
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          
          // Apply resize if enabled
          if (resizeEnabled && resizeWidth && resizeHeight) {
            canvas.width = parseInt(resizeWidth);
            canvas.height = parseInt(resizeHeight);
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create blob'));
              }
            },
            `image/${outputFormat}`,
            quality / 100
          );
        } catch (err) {
          reject(err);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    });
  };

  const handleConvertAll = async () => {
    if (files.length === 0) return;
    
    setIsConverting(true);
    setError(null);

    const startTime = Date.now();

    try {
      const updatedFiles = [...files];

      for (let i = 0; i < updatedFiles.length; i++) {
        updatedFiles[i] = { ...updatedFiles[i], status: 'converting' };
        setFiles([...updatedFiles]);

        try {
          const blob = await convertSingleImage(updatedFiles[i]);
          updatedFiles[i] = {
            ...updatedFiles[i],
            convertedBlob: blob,
            status: 'done',
          };
        } catch (err) {
          updatedFiles[i] = {
            ...updatedFiles[i],
            status: 'error',
            error: err instanceof Error ? err.message : 'Conversion failed',
          };
        }

        setFiles([...updatedFiles]);
      }

      const duration = Date.now() - startTime;
      const successCount = updatedFiles.filter(f => f.status === 'done').length;

      trackEvent('batch_conversion_completed', {
        total_files: files.length,
        successful: successCount,
        failed: files.length - successCount,
        duration_ms: duration,
        output_format: outputFormat,
        quality: quality,
        resize_enabled: resizeEnabled,
        resize_dimensions: resizeEnabled ? `${resizeWidth}x${resizeHeight}` : null,
      });
    } catch (err) {
      setError('Batch conversion failed');
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownloadAll = async () => {
    const readyFiles = files.filter(f => f.status === 'done' && f.convertedBlob);
    
    if (readyFiles.length === 0) return;

    if (readyFiles.length === 1) {
      // Single file - direct download
      const file = readyFiles[0];
      const url = URL.createObjectURL(file.convertedBlob!);
      const link = document.createElement('a');
      link.href = url;
      const name = file.original.name.substring(0, file.original.name.lastIndexOf('.')) || file.original.name;
      link.download = `${name}.${outputFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Multiple files - create ZIP
      const zip = new JSZip();
      
      for (const file of readyFiles) {
        const name = file.original.name.substring(0, file.original.name.lastIndexOf('.')) || file.original.name;
        zip.file(`${name}.${outputFormat}`, file.convertedBlob!);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pixelshift-converted-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    trackEvent('files_downloaded', {
      count: readyFiles.length,
      output_format: outputFormat,
      as_zip: readyFiles.length > 1,
    });
  };

  const UploadZone = () => (
    <div
      className={`w-full p-8 border-4 border-dashed rounded-3xl transition-all duration-300 ease-in-out cursor-pointer text-center flex flex-col items-center justify-center min-h-[400px] animate-fade-in ${isDragging ? 'border-brand-primary bg-gray-800/50' : 'border-gray-700 hover:border-brand-secondary'}`}
      onDragOver={(e) => handleDragEvents(e, true)}
      onDragEnter={(e) => handleDragEvents(e, true)}
      onDragLeave={(e) => handleDragEvents(e, false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <UploadIcon className="w-16 h-16 text-gray-500 mb-4 transition-transform duration-300 group-hover:scale-110" />
      <h3 className="text-2xl font-semibold text-white">Drag & Drop your WEBP or HEIC files</h3>
      <p className="text-gray-400 mt-2">or click to browse</p>
      <p className="text-gray-500 text-sm mt-1">Supports WEBP, HEIC, HEIF, JPG, PNG • Up to {MAX_FREE_BATCH} images</p>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/webp,image/heic,image/heif,image/jpeg,image/jpg,image/png,.heic,.heif"
        multiple
        className="hidden"
      />
      {error && <p className="text-red-500 mt-4 font-medium animate-fade-in">{error}</p>}
    </div>
  );

  const ConversionWorkspace = () => {
    const doneCount = files.filter(f => f.status === 'done').length;
    const allDone = doneCount === files.length && files.length > 0;

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Settings Panel */}
        <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
          <h3 className="text-xl font-semibold text-white mb-6">Conversion Settings</h3>
          
          {/* Format Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">Output Format</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setOutputFormat(ImageFormat.JPEG)}
                className={`flex items-center justify-center p-4 rounded-lg transition-all duration-200 border-2 ${
                  outputFormat === ImageFormat.JPEG
                    ? 'bg-brand-primary border-brand-primary text-white'
                    : 'bg-gray-700 border-gray-600 hover:border-brand-secondary'
                }`}
              >
                <JpgIcon className="w-6 h-6 mr-2" />
                <span className="font-bold text-lg">JPG</span>
              </button>
              <button
                onClick={() => setOutputFormat(ImageFormat.PNG)}
                className={`flex items-center justify-center p-4 rounded-lg transition-all duration-200 border-2 ${
                  outputFormat === ImageFormat.PNG
                    ? 'bg-brand-primary border-brand-primary text-white'
                    : 'bg-gray-700 border-gray-600 hover:border-brand-secondary'
                }`}
              >
                <PngIcon className="w-6 h-6 mr-2" />
                <span className="font-bold text-lg">PNG</span>
              </button>
            </div>
          </div>

          {/* Quality Slider (JPG only) */}
          {outputFormat === ImageFormat.JPEG && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Quality: {quality}%
                <span className="text-gray-500 text-xs ml-2">(Higher = better quality, larger file)</span>
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={quality}
                onChange={(e) => setQuality(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-brand-primary"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Small</span>
                <span>Balanced</span>
                <span>High Quality</span>
              </div>
            </div>
          )}

          {/* Resize Options */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="resize-enabled"
                checked={resizeEnabled}
                onChange={(e) => setResizeEnabled(e.target.checked)}
                className="w-4 h-4 text-brand-primary bg-gray-700 border-gray-600 rounded focus:ring-brand-primary"
              />
              <label htmlFor="resize-enabled" className="ml-2 text-sm font-medium text-gray-300">
                Resize images
              </label>
            </div>
            
            {resizeEnabled && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Width (px)</label>
                  <input
                    type="number"
                    placeholder="e.g. 1920"
                    value={resizeWidth}
                    onChange={(e) => setResizeWidth(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <span className="text-gray-400">×</span>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Height (px)</label>
                  <input
                    type="number"
                    placeholder="e.g. 1080"
                    value={resizeHeight}
                    onChange={(e) => setResizeHeight(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Convert Button */}
          <button
            onClick={handleConvertAll}
            disabled={isConverting || allDone}
            className="w-full bg-brand-primary hover:bg-brand-secondary text-white font-bold py-4 px-4 rounded-lg transition-all duration-200 disabled:bg-gray-600 disabled:cursor-not-allowed text-lg shadow-lg flex items-center justify-center"
          >
            {isConverting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Converting {doneCount}/{files.length}...
              </>
            ) : allDone ? (
              'All Converted!'
            ) : (
              `Convert ${files.length} Image${files.length > 1 ? 's' : ''}`
            )}
          </button>

          {/* Download Button */}
          {allDone && (
            <button
              onClick={handleDownloadAll}
              className="w-full mt-4 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 text-lg shadow-lg"
            >
              <DownloadIcon className="w-6 h-6 mr-2" />
              Download {files.length > 1 ? 'All (ZIP)' : 'Image'}
            </button>
          )}
        </div>

        {/* File List */}
        <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
          <h3 className="text-xl font-semibold text-white mb-4">
            Files ({files.length}/{MAX_FREE_BATCH})
          </h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {files.map((file, index) => (
              <div key={index} className="bg-gray-700 p-4 rounded-lg flex items-center justify-between">
                <div className="flex items-center flex-1 min-w-0">
                  <img 
                    src={file.previewUrl} 
                    alt={file.original.name} 
                    className="w-12 h-12 object-cover rounded mr-3"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{file.original.name}</p>
                    <p className="text-gray-400 text-xs">
                      {(file.original.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <div className="ml-3">
                  {file.status === 'pending' && (
                    <span className="text-gray-400 text-sm">Pending</span>
                  )}
                  {file.status === 'converting' && (
                    <svg className="animate-spin h-5 w-5 text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {file.status === 'done' && (
                    <span className="text-green-400 text-sm">✓ Done</span>
                  )}
                  {file.status === 'error' && (
                    <span className="text-red-400 text-sm">✗ Error</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <button
            onClick={resetState}
            className="text-gray-400 hover:text-white transition duration-200 mt-4 text-sm"
          >
            Convert different images
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {files.length === 0 ? <UploadZone /> : <ConversionWorkspace />}
    </div>
  );
};

export default ImageConverter;

